import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InsightsIcon from '@mui/icons-material/Insights';
import LockIcon from '@mui/icons-material/Lock';
import MenuIcon from '@mui/icons-material/Menu';
import PercentIcon from '@mui/icons-material/Percent';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { AppBarProps, default as MuiAppBar } from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { default as MuiDrawer } from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { styled, useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate } from '@tanstack/react-location';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import type {
    AirBnBIntegrationConfig,
    CleaningConfig,
    LockboxConfig,
    TourismTaxConfig,
} from '../../../../../../../packages/entities/Property';
import { usePropertyId } from '../../../../core/hooks/usePropertyId';
import NotificationButton from '../../../../home/components/NotificationButton';
import PropertySwitcher from '../../../../property/components/PropertySwitcher';
import UserDropdown from './UserDropdown';
import useProperty, {
    Property,
    PropertyProvider,
} from '../../../../property/hooks/useProperty';
import type { ModuleId } from '../../../../property/modules/registry';
import { apiRawFetch } from '../../../client';
import './Layout.css';

interface LockboxNavItemProps {
    propertyId: number;
    handleNavigation: (path: string) => void;
}

const LockboxNavItem = ({
    propertyId,
    handleNavigation,
}: LockboxNavItemProps) => {
    const { isLockboxEnabled, loading } = useProperty();

    if (loading || !isLockboxEnabled()) {
        return null;
    }

    return (
        <ListItemButton
            onClick={() => {
                handleNavigation(`/${propertyId}/lockbox`);
            }}
            sx={{
                mb: 0.5,
                '&:hover .MuiListItemIcon-root': {
                    transform: 'scale(1.1)',
                },
            }}
        >
            <ListItemIcon
                sx={{
                    minWidth: 40,
                    transition: 'transform 0.2s ease',
                }}
            >
                <LockIcon />
            </ListItemIcon>
            <ListItemText
                primary="Lockbox Codes"
                slotProps={{
                    primary: {
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        letterSpacing: '0.05em',
                    },
                }}
            />
        </ListItemButton>
    );
};

interface CleaningNavItemProps {
    propertyId: number;
    handleNavigation: (path: string) => void;
}

const CleaningNavItem = ({
    propertyId,
    handleNavigation,
}: CleaningNavItemProps) => {
    const { isCleaningEnabled, loading } = useProperty();

    if (loading || !isCleaningEnabled()) {
        return null;
    }

    return (
        <ListItemButton
            onClick={() => {
                handleNavigation(`/${propertyId}/cleaning`);
            }}
            sx={{
                mb: 0.5,
                '&:hover .MuiListItemIcon-root': {
                    transform: 'scale(1.1)',
                },
            }}
        >
            <ListItemIcon
                sx={{
                    minWidth: 40,
                    transition: 'transform 0.2s ease',
                }}
            >
                <CleaningServicesIcon />
            </ListItemIcon>
            <ListItemText
                primary="Cleaning"
                slotProps={{
                    primary: {
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        letterSpacing: '0.05em',
                    },
                }}
            />
        </ListItemButton>
    );
};

interface PropertyLoaderProps {
    propertyId: number;
    children: ReactNode;
}

const PropertyLoader = ({ propertyId, children }: PropertyLoaderProps) => {
    const [property, setProperty] = useState<Property | undefined>(undefined);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProperty = async () => {
            try {
                setLoading(true);
                const response = await apiRawFetch(
                    `api/properties/${propertyId}`
                );

                if (!response.ok) {
                    throw new Error('Failed to fetch property');
                }

                const data = await response.json();
                setProperty(data);
            } catch (err) {
                console.error('Failed to load property:', err);
                setProperty(undefined);
            } finally {
                setLoading(false);
            }
        };

        fetchProperty();
    }, [propertyId]);

    const isModuleEnabled = useCallback(
        (moduleId: ModuleId): boolean => {
            if (!property?.modules) {
                return false;
            }

            const config = property.modules[moduleId];
            return Boolean(
                config &&
                    typeof config === 'object' &&
                    'enabled' in config &&
                    config.enabled === true
            );
        },
        [property]
    );

    const isAirBnBEnabled = useCallback((): boolean => {
        return isModuleEnabled('integration/airbnb');
    }, [isModuleEnabled]);

    const getAirBnBConfig = useCallback(():
        | AirBnBIntegrationConfig
        | undefined => {
        if (!property?.modules) {
            return undefined;
        }
        const config = property.modules['integration/airbnb'];
        if (
            config &&
            typeof config === 'object' &&
            'enabled' in config &&
            config.enabled === true
        ) {
            return config as AirBnBIntegrationConfig;
        }
        return undefined;
    }, [property]);

    const isTourismTaxEnabled = useCallback((): boolean => {
        return isModuleEnabled('compliance/tourism-tax');
    }, [isModuleEnabled]);

    const getTourismTaxConfig = useCallback(():
        | TourismTaxConfig
        | undefined => {
        if (!property?.modules) {
            return undefined;
        }
        const config = property.modules['compliance/tourism-tax'];
        if (
            config &&
            typeof config === 'object' &&
            'enabled' in config &&
            config.enabled === true
        ) {
            return config as TourismTaxConfig;
        }
        return undefined;
    }, [property]);

    const isLockboxEnabled = useCallback((): boolean => {
        return isModuleEnabled('integration/lockbox');
    }, [isModuleEnabled]);

    const getLockboxConfig = useCallback((): LockboxConfig | undefined => {
        if (!property?.modules) {
            return undefined;
        }
        const config = property.modules['integration/lockbox'];
        if (
            config &&
            typeof config === 'object' &&
            'enabled' in config &&
            config.enabled === true
        ) {
            return config as LockboxConfig;
        }
        return undefined;
    }, [property]);

    const isCleaningEnabled = useCallback((): boolean => {
        return isModuleEnabled('operations/cleaning');
    }, [isModuleEnabled]);

    const isSzamlazzEnabled = useCallback((): boolean => {
        return isModuleEnabled('integration/szamlazz');
    }, [isModuleEnabled]);

    const getCleaningConfig = useCallback((): CleaningConfig | undefined => {
        if (!property?.modules) {
            return undefined;
        }
        const config = property.modules['operations/cleaning'];
        if (
            config &&
            typeof config === 'object' &&
            'enabled' in config &&
            config.enabled === true
        ) {
            return config as CleaningConfig;
        }
        return undefined;
    }, [property]);

    return (
        <PropertyProvider
            value={{
                propertyId,
                property,
                isModuleEnabled,
                isAirBnBEnabled,
                getAirBnBConfig,
                isTourismTaxEnabled,
                getTourismTaxConfig,
                isLockboxEnabled,
                getLockboxConfig,
                isCleaningEnabled,
                getCleaningConfig,
                isSzamlazzEnabled,
                loading,
            }}
        >
            {children}
        </PropertyProvider>
    );
};

const drawerWidth = 250;

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop: string) => prop !== 'open',
})<AppBarProps & { open: boolean }>(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.up('md')]: {
        ...(open && {
            marginLeft: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            transition: theme.transitions.create(['width', 'margin'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
        }),
    },
}));

const PermanentDrawer = styled(MuiDrawer, {
    shouldForwardProp: (prop: string) => prop !== 'open',
})(({ theme, open }) => ({
    '& .MuiDrawer-paper': {
        position: 'relative',
        whiteSpace: 'nowrap',
        width: drawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        boxSizing: 'border-box',
        ...(!open && {
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            width: theme.spacing(7),
            [theme.breakpoints.up('sm')]: {
                width: theme.spacing(8.25),
            },
        }),
    },
}));

interface Props {
    children: ReactNode;
}

const Layout = ({ children }: Props) => {
    const propertyId = usePropertyId();
    const navigate = useNavigate();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [open, setOpen] = useState(false);
    const toggleDrawer = () => {
        setOpen(!open);
    };

    const handleNavigation = (path: string) => {
        navigate({ to: path, replace: false });
        if (isSmallScreen) {
            setOpen(false);
        }
    };

    const drawerContent = (
        <>
            <Toolbar
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: open ? 2 : 1,
                    py: 1.55,
                    borderBottom: '3px solid #6366f1',
                    backgroundColor: '#0f0f23',
                }}
            >
                {open && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                        }}
                    >
                        <Box
                            component="img"
                            src="/mushroom-logo.svg"
                            alt="MushRoom Logo"
                            sx={{
                                height: 32,
                                width: 32,
                            }}
                        />
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 900,
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                fontSize: '1rem',
                                color: '#6366f1',
                            }}
                        >
                            MushRoom
                        </Typography>
                    </Box>
                )}
                <IconButton
                    onClick={toggleDrawer}
                    sx={{ ml: open ? 0 : 'auto' }}
                >
                    {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </IconButton>
            </Toolbar>
            <List component="nav" sx={{ px: 0 }}>
                <ListItemButton
                    onClick={() => {
                        handleNavigation(
                            propertyId ? `/${propertyId}/home` : '/'
                        );
                    }}
                    sx={{
                        mb: 0.5,
                        '&:hover .MuiListItemIcon-root': {
                            transform: 'scale(1.1)',
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 40,
                            transition: 'transform 0.2s ease',
                        }}
                    >
                        <DashboardIcon />
                    </ListItemIcon>
                    <ListItemText
                        primary="Home"
                        slotProps={{
                            primary: {
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                letterSpacing: '0.05em',
                            },
                        }}
                    />
                </ListItemButton>
                {propertyId && (
                    <CleaningNavItem
                        propertyId={propertyId}
                        handleNavigation={handleNavigation}
                    />
                )}
                <ListItemButton
                    onClick={() => {
                        handleNavigation(
                            propertyId ? `/${propertyId}/stats` : '/'
                        );
                    }}
                    sx={{
                        mb: 0.5,
                        '&:hover .MuiListItemIcon-root': {
                            transform: 'scale(1.1)',
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 40,
                            transition: 'transform 0.2s ease',
                        }}
                    >
                        <InsightsIcon />
                    </ListItemIcon>
                    <ListItemText
                        primary="Stats"
                        slotProps={{
                            primary: {
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                letterSpacing: '0.05em',
                            },
                        }}
                    />
                </ListItemButton>
                <ListItemButton
                    onClick={() => {
                        handleNavigation(
                            propertyId ? `/${propertyId}/financial` : '/'
                        );
                    }}
                    sx={{
                        mb: 0.5,
                        '&:hover .MuiListItemIcon-root': {
                            transform: 'scale(1.1)',
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 40,
                            transition: 'transform 0.2s ease',
                        }}
                    >
                        <AccountBalanceIcon />
                    </ListItemIcon>
                    <ListItemText
                        primary="Financial"
                        slotProps={{
                            primary: {
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                letterSpacing: '0.05em',
                            },
                        }}
                    />
                </ListItemButton>
                <ListItemButton
                    onClick={() => {
                        handleNavigation(
                            propertyId ? `/${propertyId}/expenses` : '/'
                        );
                    }}
                    sx={{
                        mb: 0.5,
                        '&:hover .MuiListItemIcon-root': {
                            transform: 'scale(1.1)',
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 40,
                            transition: 'transform 0.2s ease',
                        }}
                    >
                        <ReceiptLongIcon />
                    </ListItemIcon>
                    <ListItemText
                        primary="Expenses"
                        slotProps={{
                            primary: {
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                letterSpacing: '0.05em',
                            },
                        }}
                    />
                </ListItemButton>
                <ListItemButton
                    onClick={() => {
                        handleNavigation(
                            propertyId ? `/${propertyId}/financial/tax-payments` : '/'
                        );
                    }}
                    sx={{
                        mb: 0.5,
                        '&:hover .MuiListItemIcon-root': {
                            transform: 'scale(1.1)',
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 40,
                            transition: 'transform 0.2s ease',
                        }}
                    >
                        <PercentIcon />
                    </ListItemIcon>
                    <ListItemText
                        primary="Tax Payments"
                        slotProps={{
                            primary: {
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                letterSpacing: '0.05em',
                            },
                        }}
                    />
                </ListItemButton>
                {propertyId && (
                    <LockboxNavItem
                        propertyId={propertyId}
                        handleNavigation={handleNavigation}
                    />
                )}
                <ListItemButton
                    onClick={() => {
                        handleNavigation(
                            propertyId ? `/${propertyId}/calendar` : '/'
                        );
                    }}
                    sx={{
                        mb: 0.5,
                        '&:hover .MuiListItemIcon-root': {
                            transform: 'scale(1.1)',
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 40,
                            transition: 'transform 0.2s ease',
                        }}
                    >
                        <CalendarMonthIcon />
                    </ListItemIcon>
                    <ListItemText
                        primary="Calendar"
                        slotProps={{
                            primary: {
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                letterSpacing: '0.05em',
                            },
                        }}
                    />
                </ListItemButton>
            </List>
        </>
    );

    const layoutContent = (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar position="absolute" open={open}>
                <Toolbar
                    sx={{
                        pr: '24px', // keep right padding when drawer closed
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="open drawer"
                        onClick={toggleDrawer}
                        sx={{
                            marginRight: '36px',
                            ...(!isSmallScreen && open && { display: 'none' }),
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Box sx={{ flexGrow: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {propertyId && <NotificationButton />}
                        {propertyId && (
                            <PropertySwitcher currentPropertyId={propertyId} />
                        )}
                        <UserDropdown />
                    </Box>
                </Toolbar>
            </AppBar>
            {isSmallScreen ? (
                <MuiDrawer
                    variant="temporary"
                    open={open}
                    onClose={toggleDrawer}
                    ModalProps={{
                        keepMounted: true, // Better mobile performance
                    }}
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                        },
                    }}
                >
                    {drawerContent}
                </MuiDrawer>
            ) : (
                <PermanentDrawer variant="permanent" open={open}>
                    {drawerContent}
                </PermanentDrawer>
            )}
            <Box
                component="main"
                sx={{
                    backgroundColor: '#0f0f23',
                    flexGrow: 1,
                    height: '100vh',
                    overflow: 'auto',
                    position: 'relative',
                    pt: 10,
                    pb: 4,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background:
                            'linear-gradient(90deg, #6366f1 0%, #f59e0b 100%)',
                        zIndex: 1,
                    },
                }}
            >
                {children}
            </Box>
        </Box>
    );

    if (propertyId) {
        return (
            <PropertyLoader propertyId={propertyId}>
                {layoutContent}
            </PropertyLoader>
        );
    }

    return (
        <PropertyProvider
            value={{
                propertyId: undefined,
                isModuleEnabled: () => false,
                isAirBnBEnabled: () => false,
                getAirBnBConfig: () => undefined,
                isTourismTaxEnabled: () => false,
                getTourismTaxConfig: () => undefined,
                isLockboxEnabled: () => false,
                getLockboxConfig: () => undefined,
                isCleaningEnabled: () => false,
                getCleaningConfig: () => undefined,
                isSzamlazzEnabled: () => false,
            }}
        >
            {layoutContent}
        </PropertyProvider>
    );
};

export default Layout;
